import { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DataGrid, ptBR } from '@mui/x-data-grid';
import Main from 'components/Main/Main';
import useConciliacaoFlow from './useConciliacaoFlow';
import {
  ActionDialogs,
  DetailDrawer,
  FlowFilterFields,
  FilterHeader,
  money
} from './ConciliacaoFlowModelParts';
import './ConciliacaoFlowModeloTorre.scss';

const StatCard = ({ eyebrow, value, caption, icon, tone, highlight = false }) => (
  <Box className={`tower-stat-card ${tone}${highlight ? ' is-highlight' : ''}`}>
    <Box className="tower-stat-icon">{icon}</Box>
    <Box className="tower-stat-copy">
      <Typography className="tower-stat-eyebrow">{eyebrow}</Typography>
      <Typography className="tower-stat-value">{value}</Typography>
      <Typography className="tower-stat-caption">{caption}</Typography>
    </Box>
  </Box>
);

const ConciliacaoFlowModeloTorre = ({ escopoAcesso, setEscopoAcesso }) => {
  const conciliacaoFlow = useConciliacaoFlow();
  const {
    filters,
    setFilters,
    rows,
    rowCount,
    loading,
    error,
    selected,
    setSelected,
    actionRow,
    setActionRow,
    confirm,
    setConfirm,
    motivo,
    setMotivo,
    load,
    action,
    confirmAction,
    actionLoading,
    filterOpen,
    setFilterOpen,
    stats,
    filtersCount,
    clearFilters,
    applyFilters,
    paginationModel,
    sortModel,
    handlePaginationModelChange,
    handleSortModelChange,
    columns,
    columnGroupingModel,
    parceiros,
    carregandoParceiros,
    vencimentoDivergente,
    selecaoLoteAtiva,
    alternarSelecaoLote,
    titulosSelecionados,
    setTitulosSelecionados,
    abrirCapturaLote,
    podeCapturar
  } = conciliacaoFlow;

  return (
    <Main
      title="Conciliação Flow · Control Tower"
      className="conciliacao-flow"
      escopoAcesso={escopoAcesso}
      setEscopoAcesso={setEscopoAcesso}
    >
      <Box className="tower-page">
        <Box className="tower-hero">
          <Box className="tower-hero-glow" />
          <Box className="tower-hero-content">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box className="tower-brand-mark">
                <AutoGraphOutlinedIcon />
              </Box>
              <Box>
                <Typography className="tower-title">Conciliação Flow</Typography>
                <Typography className="tower-subtitle">
                  Uma visão completa para acompanhar cada título até o fechamento financeiro.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} className="tower-hero-actions">
              <Button
                variant="outlined"
                className="btn-atualizar-grid-conciliacao"
                startIcon={<RefreshRoundedIcon />}
                onClick={() => load(filters, paginationModel, sortModel)}
                disabled={loading}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                className="btn-abrir-filtros-conciliacao"
                startIcon={<FilterAltOutlinedIcon />}
                onClick={() => setFilterOpen(true)}
              >
                Filtros {filtersCount > 0 && <span className="tower-filter-count">{filtersCount}</span>}
              </Button>
            </Stack>
          </Box>
          <Box className="tower-hero-orbit orbit-one" />
          <Box className="tower-hero-orbit orbit-two" />
        </Box>

        <Grid container spacing={1.5} className="tower-stats">
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              eyebrow="Carteira monitorada"
              value={money(stats.totalValue)}
              caption={`${rows.length} títulos no período atual`}
              icon={<TrendingUpRoundedIcon />}
              tone="blue"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              eyebrow="Prontos para captura"
              value={stats.available}
              caption="Aguardando ação operacional"
              icon={<CheckCircleOutlineRoundedIcon />}
              tone="green"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              eyebrow="Ciclo concluído"
              value={stats.captured}
              caption="Capturados ou conciliados"
              icon={<AutoGraphOutlinedIcon />}
              tone="violet"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              eyebrow="Pontos de atenção"
              value={stats.attention}
              caption="Informações divergentes"
              icon={<WarningAmberRoundedIcon />}
              tone="orange"
              highlight={stats.attention > 0}
            />
          </Grid>
        </Grid>

        <Box className="tower-content-card">
          <Stack
            className="tower-table-toolbar"
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography className="tower-section-title">Títulos para conciliação</Typography>
              <Typography className="tower-section-caption">
                Selecione uma linha para abrir os detalhes ou acesse as ações rápidas.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} className="tower-captura-lote-acoes">
              {selecaoLoteAtiva ? (
                <>
                  <Button
                    variant="text"
                    className="btn-cancelar-captura-lote"
                    startIcon={<CloseRoundedIcon />}
                    onClick={alternarSelecaoLote}
                    disabled={actionLoading}
                  >
                    Cancelar seleção
                  </Button>
                  <Button
                    variant="contained"
                    className="btn-capturar-selecionados"
                    startIcon={<CheckCircleOutlineRoundedIcon />}
                    onClick={abrirCapturaLote}
                    disabled={!titulosSelecionados.length || actionLoading || loading}
                  >
                    Capturar selecionados ({titulosSelecionados.length})
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  className="btn-captura-lote"
                  startIcon={<ChecklistRoundedIcon />}
                  onClick={alternarSelecaoLote}
                >
                  Captura em lote
                </Button>
              )}
            </Stack>
          </Stack>
          {error && (
            <Alert severity="error" className="tower-alert">
              {error}
            </Alert>
          )}
          {loading ? (
            <Stack spacing={1} className="tower-loading">
              {[1, 2, 3, 4].map(item => (
                <Skeleton key={item} variant="rounded" height={62} />
              ))}
            </Stack>
          ) : (
            <Box className="tower-grid-wrap">
              <DataGrid
                rows={rows}
                rowCount={rowCount}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                experimentalFeatures={{ columnGrouping: true }}
                autoHeight
                rowHeight={68}
                columnHeaderHeight={48}
                pageSizeOptions={[10, 15, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                paginationMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                sortingMode="server"
                localeText={{ ...ptBR.components.MuiDataGrid.defaultProps.localeText }}
                disableRowSelectionOnClick
                checkboxSelection={selecaoLoteAtiva}
                isRowSelectable={params => podeCapturar(params.row)}
                rowSelectionModel={titulosSelecionados}
                onRowSelectionModelChange={setTitulosSelecionados}
                getRowClassName={params =>
                  vencimentoDivergente(params.row) ? 'tower-row-divergente' : ''
                }
                onRowClick={params => setSelected(params.row)}
                slots={{
                  noRowsOverlay: () => (
                    <Box className="tower-empty-state">
                      <SearchRoundedIcon />
                      <Typography>Nenhum título encontrado com esses critérios.</Typography>
                      <Button size="small" onClick={clearFilters}>
                        Limpar filtros
                      </Button>
                    </Box>
                  )
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        className="tower-filter-drawer"
      >
        <Box className="tower-filter-content">
          <FilterHeader onClose={() => setFilterOpen(false)} />
          <FlowFilterFields
            filters={filters}
            setFilters={setFilters}
            parceiros={parceiros}
            carregandoParceiros={carregandoParceiros}
          />
          <Stack direction="row" spacing={1} className="tower-filter-actions">
            <Button
              variant="text"
              className="limpar-filtros"
              onClick={clearFilters}
              startIcon={<RefreshRoundedIcon />}
            >
              Limpar tudo
            </Button>
            <Button
              variant="contained"
              className="pesquisar-filtros"
              onClick={() => {
                setFilterOpen(false);
                applyFilters(filters);
              }}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} color="inherit" /> : <SearchRoundedIcon />
              }
            >
              Aplicar filtros
            </Button>
          </Stack>
        </Box>
      </Drawer>
      <DetailDrawer
        item={selected}
        onClose={() => setSelected(null)}
        onAction={action}
        actionLoading={actionLoading}
      />
      <ActionDialogs
        actionRow={actionRow}
        setActionRow={setActionRow}
        action={action}
        confirm={confirm}
        setConfirm={setConfirm}
        motivo={motivo}
        setMotivo={setMotivo}
        confirmAction={confirmAction}
        actionLoading={actionLoading}
      />
    </Main>
  );
};

export default memo(ConciliacaoFlowModeloTorre);
